import {
    Box,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Stack,
    Switch,
    ThemeColorVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
// eslint-disable-next-line caspeco-react/discourage-chakra-import
import { ScaleFade } from "@chakra-ui/react";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { validateCustomerName, validateEmail } from "helpers/validation-helper";
import { useBookingActionsContext, useBookingStateContext } from "hooks/use-booking-state";
import { ChangeEvent, useState } from "react";
import { Control, useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ContactInfoForm } from "store/store-types";

interface ICustomerFieldsProps {
    control: Control<ContactInfoForm, string>;
}

/**
 * Form fields for customer information including company name and optional invoice fields.
 * Displays conditionally based on user toggles for company customer and wish to be invoiced.
 * @param control - React Hook Form control object for managing form state
 */
const CustomerFields = ({ control }: ICustomerFieldsProps) => {
    const { t } = useTranslation();
    const { data: settings } = useExternalBookingSettings();
    const { setCustomer, setWishToBeInvoiced, setInvoiceMarking } = useBookingActionsContext();
    const state = useBookingStateContext();
    const {
        customer,
        invoiceMarking: stateInvoiceMarking,
        wishToBeInvoiced: stateWishToBeInvoiced,
    } = state;
    const [isCompanyCustomer, setIsCompanyCustomer] = useState(!!customer?.name);
    const [wishToBeInvoiced, setWishToBeInvoicedLocal] = useState(stateWishToBeInvoiced ?? false);

    const {
        fieldState: { error },
        field,
    } = useController({
        name: "name",
        control,
        rules: {
            required: isCompanyCustomer ? t("customer.customerName") : false,
            validate: isCompanyCustomer ? (value) => validateCustomerName(value) : undefined,
        },
    });

    const {
        fieldState: { error: orgNrError },
        field: orgNrField,
    } = useController({
        name: "orgNr",
        control,
        rules: {
            required: wishToBeInvoiced ? t("customer.validation.organizationCode") : false,
        },
    });

    const {
        fieldState: { error: invoiceAddressError },
        field: invoiceAddressField,
    } = useController({
        name: "invoiceStreetaddress",
        control,
        rules: {
            required: wishToBeInvoiced ? t("customer.validation.invoiceStreetAddress") : false,
        },
    });

    const {
        fieldState: { error: invoiceZipCodeError },
        field: invoiceZipCodeField,
    } = useController({
        name: "invoiceZipCode",
        control,
        rules: {
            required: wishToBeInvoiced ? t("customer.validation.invoiceZipCode") : false,
        },
    });

    const {
        fieldState: { error: invoiceCityError },
        field: invoiceCityField,
    } = useController({
        name: "invoiceCity",
        control,
        rules: {
            required: wishToBeInvoiced ? t("customer.validation.invoiceCity") : false,
        },
    });

    const {
        fieldState: { error: invoiceEmailError },
        field: invoiceEmailField,
    } = useController({
        name: "invoiceEmail",
        control,
        rules: {
            required: wishToBeInvoiced ? t("customer.validation.email") : false,
            validate: wishToBeInvoiced ? (value) => validateEmail(value) : undefined,
        },
    });

    const {
        fieldState: { error: invoiceMarkingError },
        field: invoiceMarkingField,
    } = useController({
        name: "invoiceMarking",
        control,
    });

    // React Hook Form keeps values for unmounted controllers, so clear them alongside
    // the store — otherwise validation passes on stale values after toggling back on.
    const clearInvoiceFormValues = () => {
        orgNrField.onChange("");
        invoiceAddressField.onChange("");
        invoiceZipCodeField.onChange("");
        invoiceCityField.onChange("");
        invoiceEmailField.onChange("");
        invoiceMarkingField.onChange("");
    };

    const handleCustomerTypeToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setIsCompanyCustomer(checked);
        if (!checked) {
            setWishToBeInvoicedLocal(false);
            setWishToBeInvoiced?.(false);
            setCustomer?.({});
            setInvoiceMarking?.("");
            field.onChange("");
            clearInvoiceFormValues();
        }
    };

    const handleInvoiceToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setWishToBeInvoicedLocal(checked);
        setWishToBeInvoiced?.(checked);
        if (!checked) {
            setCustomer?.({
                ...customer,
                orgNr: undefined,
                invoiceEmail: undefined,
                invoiceStreetaddress: undefined,
                invoiceZipCode: undefined,
                invoiceCity: undefined,
            });
            setInvoiceMarking?.("");
            clearInvoiceFormValues();
        }
    };

    const handleCustomerNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        field.onChange(e.target.value);
        setCustomer?.({ ...customer, name: value });
    };

    const handleOrgNrChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        orgNrField.onChange(value);
        setCustomer?.({ ...customer, orgNr: value });
    };

    const handleInvoiceAddressChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        invoiceAddressField.onChange(value);
        setCustomer?.({ ...customer, invoiceStreetaddress: value });
    };

    const handleInvoiceZipCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        invoiceZipCodeField.onChange(value);
        setCustomer?.({ ...customer, invoiceZipCode: value });
    };

    const handleInvoiceCityChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        invoiceCityField.onChange(value);
        setCustomer?.({ ...customer, invoiceCity: value });
    };

    const handleInvoiceEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        invoiceEmailField.onChange(value);
        setCustomer?.({ ...customer, invoiceEmail: value });
    };

    const handleInvoiceMarkingChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        invoiceMarkingField.onChange(value);
        setInvoiceMarking?.(value);
    };

    const enableInvoicing = settings?.enableInvoicing;
    return (
        <>
            <FormControl>
                <Flex direction="row" align="center" gap={ThemeSpaceVariable.Small}>
                    <Switch
                        isChecked={isCompanyCustomer}
                        onChange={handleCustomerTypeToggle}
                        id="isCompanyCustomer"
                        color="primary"
                    />
                    <FormLabel m={0}>{t("customer.addCustomerName")}</FormLabel>
                </Flex>
            </FormControl>
            {isCompanyCustomer && (
                <ScaleFade in={isCompanyCustomer}>
                    <FormControl isInvalid={!!error}>
                        <FormLabel whiteSpace="nowrap">
                            {t("customer.name")}{" "}
                            <Box as="span" color={ThemeColorVariable.Error}>
                                *
                            </Box>
                        </FormLabel>
                        <Input
                            size="lg"
                            id="customerName"
                            name="customerName"
                            autoComplete="customerName"
                            onChange={handleCustomerNameChange}
                            value={customer?.name || ""}
                            onBlur={field.onBlur}
                            ref={field.ref}
                        />
                        <FormErrorMessage>{error?.message}</FormErrorMessage>
                    </FormControl>
                </ScaleFade>
            )}
            {enableInvoicing && isCompanyCustomer && (
                <ScaleFade in={isCompanyCustomer}>
                    <FormControl>
                        <Flex direction="row" align="center" gap={ThemeSpaceVariable.Small}>
                            <Switch
                                isChecked={wishToBeInvoiced}
                                onChange={handleInvoiceToggle}
                                id="wishToBeInvoiced"
                                color="primary"
                            />
                            <FormLabel m={0}>{t("customer.wishToBeInvoiced")}</FormLabel>
                        </Flex>
                    </FormControl>
                </ScaleFade>
            )}
            {wishToBeInvoiced && (
                <>
                    <ScaleFade in={wishToBeInvoiced}>
                        <Stack gap={ThemeSpaceVariable.Medium}>
                            <Stack
                                direction="row"
                                flexWrap={{ base: "wrap", md: "nowrap" }}
                                gap={ThemeSpaceVariable.Medium}
                            >
                                <FormControl isInvalid={!!orgNrError}>
                                    <FormLabel whiteSpace="nowrap">
                                        {t("customer.organizationCode")}{" "}
                                        <Box as="span" color={ThemeColorVariable.Error}>
                                            *
                                        </Box>
                                    </FormLabel>
                                    <Input
                                        size="lg"
                                        id="orgNr"
                                        name="orgNr"
                                        autoComplete="organization"
                                        onChange={handleOrgNrChange}
                                        value={customer?.orgNr || ""}
                                        onBlur={orgNrField.onBlur}
                                        ref={orgNrField.ref}
                                    />
                                    <FormErrorMessage>{orgNrError?.message}</FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={!!invoiceAddressError}>
                                    <FormLabel whiteSpace="nowrap">
                                        {t("customer.invoiceStreetAddress")}{" "}
                                        <Box as="span" color={ThemeColorVariable.Error}>
                                            *
                                        </Box>
                                    </FormLabel>
                                    <Input
                                        size="lg"
                                        id="invoiceStreetaddress"
                                        name="invoiceStreetaddress"
                                        autoComplete="street-address"
                                        onChange={handleInvoiceAddressChange}
                                        value={customer?.invoiceStreetaddress || ""}
                                        onBlur={invoiceAddressField.onBlur}
                                        ref={invoiceAddressField.ref}
                                    />
                                    <FormErrorMessage>
                                        {invoiceAddressError?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>
                            <Stack
                                direction="row"
                                flexWrap={{ base: "wrap", md: "nowrap" }}
                                gap={ThemeSpaceVariable.Medium}
                            >
                                <FormControl isInvalid={!!invoiceZipCodeError}>
                                    <FormLabel whiteSpace="nowrap">
                                        {t("customer.invoiceZipCode")}{" "}
                                        <Box as="span" color={ThemeColorVariable.Error}>
                                            *
                                        </Box>
                                    </FormLabel>
                                    <Input
                                        size="lg"
                                        id="invoiceZipCode"
                                        name="invoiceZipCode"
                                        autoComplete="postal-code"
                                        onChange={handleInvoiceZipCodeChange}
                                        value={customer?.invoiceZipCode || ""}
                                        onBlur={invoiceZipCodeField.onBlur}
                                        ref={invoiceZipCodeField.ref}
                                    />
                                    <FormErrorMessage>
                                        {invoiceZipCodeError?.message}
                                    </FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={!!invoiceCityError}>
                                    <FormLabel whiteSpace="nowrap">
                                        {t("customer.invoiceCity")}{" "}
                                        <Box as="span" color={ThemeColorVariable.Error}>
                                            *
                                        </Box>
                                    </FormLabel>
                                    <Input
                                        size="lg"
                                        id="invoiceCity"
                                        name="invoiceCity"
                                        autoComplete="address-level2"
                                        onChange={handleInvoiceCityChange}
                                        value={customer?.invoiceCity || ""}
                                        onBlur={invoiceCityField.onBlur}
                                        ref={invoiceCityField.ref}
                                    />
                                    <FormErrorMessage>{invoiceCityError?.message}</FormErrorMessage>
                                </FormControl>
                            </Stack>
                            <FormControl isInvalid={!!invoiceEmailError}>
                                <FormLabel whiteSpace="nowrap">
                                    {t("customer.email")}{" "}
                                    <Box as="span" color={ThemeColorVariable.Error}>
                                        *
                                    </Box>
                                </FormLabel>
                                <Input
                                    size="lg"
                                    id="invoiceEmail"
                                    name="invoiceEmail"
                                    type="email"
                                    autoComplete="email"
                                    onChange={handleInvoiceEmailChange}
                                    value={customer?.invoiceEmail || ""}
                                    onBlur={invoiceEmailField.onBlur}
                                    ref={invoiceEmailField.ref}
                                />
                                <FormErrorMessage>{invoiceEmailError?.message}</FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={!!invoiceMarkingError}>
                                <FormLabel>{t("customer.invoiceMarking")}</FormLabel>
                                <Input
                                    size="lg"
                                    id="invoiceMarking"
                                    name="invoiceMarking"
                                    autoComplete="off"
                                    onChange={handleInvoiceMarkingChange}
                                    value={stateInvoiceMarking || ""}
                                    onBlur={invoiceMarkingField.onBlur}
                                    ref={invoiceMarkingField.ref}
                                />
                                <FormErrorMessage>{invoiceMarkingError?.message}</FormErrorMessage>
                            </FormControl>
                        </Stack>
                    </ScaleFade>
                </>
            )}
        </>
    );
};

export default CustomerFields;
